import { useEffect, useRef, useState } from "react";
import { FiRotateCcw } from "react-icons/fi";

export interface UndoToastProps {
  open: boolean;
  message: string;
  /** Acción de deshacer, p. ej. "Deshacer". Sin ella, el aviso es solo informativo. */
  actionLabel?: string;
  onAction?: () => void;
  /** Se cierra solo tras este tiempo; se pausa con hover o foco. */
  onDismiss: () => void;
  durationMs?: number;
}

/**
 * Aviso temporal con deshacer, anclado abajo al centro: 96px sobre la barra de pestañas en móvil, 40px en md+.
 * Se cierra solo tras `durationMs`; el tiempo restante se pausa mientras el puntero o el foco están dentro.
 */
export function UndoToast({ open, message, actionLabel, onAction, onDismiss, durationMs = 10000 }: UndoToastProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;
  const remainingRef = useRef(durationMs);
  const startedAtRef = useRef(0);
  const dismissRef = useRef(onDismiss);

  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!open) {
      remainingRef.current = durationMs;
      return undefined;
    }
    if (paused) return undefined;
    startedAtRef.current = Date.now();
    const timer = window.setTimeout(() => dismissRef.current(), remainingRef.current);
    return () => {
      window.clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
    };
  }, [open, paused, durationMs]);

  return (
    <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-24 z-[70] flex justify-center px-4 md:bottom-10">
      {open ? (
        <div
          className="app-focus-ring pointer-events-auto flex w-full max-w-[420px] items-center gap-3 rounded-[var(--ds-radius-md)] bg-[var(--ds-ink)] py-2 pl-4 pr-2 text-[15px] font-medium leading-5 text-[var(--ds-ground)] shadow-[var(--ds-shadow-3)]"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
          }}
        >
          <span className="min-w-0 flex-1">{message}</span>
          {onAction && actionLabel ? (
            <button
              type="button"
              onClick={onAction}
              className="app-focus-ring inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full px-4 text-[15px] font-medium leading-5 underline-offset-4 hover:underline"
            >
              <FiRotateCcw size={18} aria-hidden="true" />
              {actionLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
